/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericSearchableDropdown from "@ui-components/Dropdown/GenericSearchableDropdown";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericInput from "@ui-components/Input/GenericInput";
import { Reflector } from "./ClusterModal";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import CustomRadioGroup from "@ui-components/RadioGroup/CustomRadioGroup";
import GenericInlineAlert from "@ui-components/Alert/GenericInlineAlert";
import { getInterfacesAddresses } from "../request/request";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector } from "@redux/hooks";

interface ClusterStep3Props {
  clusterName: string;
  description: string;
  selectedReflectors: Reflector[];
  roles: Record<string, { role?: string; address?: string; interfaceAddressId?: number; port?: number }>;
  setRoles: (val: Record<string, { role?: string; address?: string; interfaceAddressId?: number; port?: number }>) => void;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  isActive?: boolean; // Add this to know when step 3 is active
}

const ClusterStep3: React.FC<ClusterStep3Props> = ({ clusterName, description, selectedReflectors, roles, setRoles, onBack, onSubmit, isSubmitting = false, isActive = true }) => {
  const [roleSwapNotification, setRoleSwapNotification] = useState<string | null>(null);
  const { tenantId } = useAppSelector((state: any) => state.selection);
  const [interfaceAddresses, setInterfaceAddresses] = useState<Record<string, string[]>>({});
  const [interfaceData, setInterfaceData] = useState<Record<string, any[]>>({});
  const [loadingAddresses, setLoadingAddresses] = useState<Record<string, boolean>>({});
  const { getAccessTokenSilently } = useAuth0();

  // Fetch interface addresses for each reflector
  useEffect(() => {
    const fetchInterfaceAddresses = async () => {
      // Only fetch if we're on step 3 and have reflectors
      if (!isActive || selectedReflectors.length === 0) {
        return;
      }

      for (const reflector of selectedReflectors) {
        setLoadingAddresses(prev => ({ ...prev, [reflector.id]: true }));
        try {
          if (!tenantId) continue;
          const token = await getAccessTokenSilently();
          const response = await getInterfacesAddresses(token, Number(reflector.id), tenantId);
          
          // Extract IP addresses from the response data array with interface names
          // The actual data is nested in response.data.data
          const rawData = response.data?.data || [];
          const addresses = rawData.map((item: any) => `${item.ipAddress} (${item.interface})`);
          
          setInterfaceAddresses(prev => ({ 
            ...prev, 
            [reflector.id]: addresses 
          }));
          
          // Store the full interface data for later use
          setInterfaceData(prev => ({
            ...prev,
            [reflector.id]: rawData
          }));
        } catch (error) {
          console.error(`Error fetching interface addresses for reflector ${reflector.id}:`, error);
          setInterfaceAddresses(prev => ({ 
            ...prev, 
            [reflector.id]: [] 
          }));
        } finally {
          setLoadingAddresses(prev => ({ ...prev, [reflector.id]: false }));
        }
      }
    };

    fetchInterfaceAddresses();
  }, [selectedReflectors, isActive, getAccessTokenSilently]);

  // Clear interface data when reflectors change (when going back to step 2 and changing selection)
  useEffect(() => {
    if (isActive) {
      // Reset interface data for reflectors that are no longer selected
      const currentReflectorIds = selectedReflectors.map(r => r.id);
      setInterfaceAddresses(prev => {
        const newData: Record<string, string[]> = {};
        currentReflectorIds.forEach(id => {
          if (prev[id]) {
            newData[id] = prev[id];
          }
        });
        return newData;
      });
      setInterfaceData(prev => {
        const newData: Record<string, any[]> = {};
        currentReflectorIds.forEach(id => {
          if (prev[id]) {
            newData[id] = prev[id];
          }
        });
        return newData;
      });
    }
  }, [selectedReflectors, isActive]);

  const handleRoleChange = (id: string, role: string) => {
    // Convert role to lowercase
    const lowerRole = role.toLowerCase();
    
    // Create new roles object
    const newRoles = { ...roles };
    
    // Check if this reflector was previously secondary and is now becoming primary
    const wasSecondary = roles[id]?.role === 'secondary';
    const currentPrimary = Object.keys(roles).find(key => roles[key]?.role === 'primary');
    
    // If selecting primary
    if (lowerRole === 'primary') {
      // If there was a previous primary and this reflector was secondary, show swap notification
      if (currentPrimary && wasSecondary) {
        setRoleSwapNotification(`Roles swapped: ${selectedReflectors.find(r => r.id === currentPrimary)?.name} is now Secondary, ${selectedReflectors.find(r => r.id === id)?.name} is now Primary`);
        // Clear notification after 3 seconds
        setTimeout(() => setRoleSwapNotification(null), 3000);
      }
      
      // Set all other reflectors to secondary
      selectedReflectors.forEach(reflector => {
        if (reflector.id !== id) {
          newRoles[reflector.id] = { ...newRoles[reflector.id], role: 'secondary' };
        }
      });
    }
    
    // Set the selected role
    newRoles[id] = { ...newRoles[id], role: lowerRole };
    
    setRoles(newRoles);
  };

  // Auto-assign secondary roles for reflectors without roles and set default port
  React.useEffect(() => {
    const newRoles = { ...roles };
    let hasChanges = false;
    
    selectedReflectors.forEach(reflector => {
      if (!roles[reflector.id]?.role) {
        newRoles[reflector.id] = { ...newRoles[reflector.id], role: 'secondary' };
        hasChanges = true;
      }
      // Set default port if not already set
      if (!roles[reflector.id]?.port) {
        newRoles[reflector.id] = { ...newRoles[reflector.id], port: 10117 };
        hasChanges = true;
      }
    });
    
    if (hasChanges) {
      setRoles(newRoles);
    }
  }, [selectedReflectors, roles, setRoles]);

  const handleAddressChange = (id: string, selectedDisplayString: string) => {
    // Find the corresponding interface data for this reflector
    const reflectorData = interfaceData[id] || [];
    const selectedInterface = reflectorData.find(item => 
      `${item.ipAddress} (${item.interface})` === selectedDisplayString
    );
    
    // Store both the display string and the interfaceAddressId
    setRoles({ 
      ...roles, 
      [id]: { 
        ...roles[id], 
        address: selectedDisplayString,
        interfaceAddressId: selectedInterface?.interfaceAddressId 
      } 
    });
  };

  const handlePortChange = (id: string, port: string) => {
    setRoles({ 
      ...roles, 
      [id]: { 
        ...roles[id], 
        port: port ? parseInt(port) : 10117 
      } 
    });
  };

  // Validate that exactly one primary is selected
  const hasPrimary = Object.values(roles).some(role => role.role === 'primary');
  const canSubmit = hasPrimary;

  return (
    <div className="flex flex-col gap-4 pt-4">
      <h2 className="text-body-large font-bold mb-1">Cluster summary</h2>
      
      {/* Role swap notification */}
      {roleSwapNotification && (
        <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
          <div className="text-blue-700 text-sm">
            <strong>Role Change:</strong> {roleSwapNotification}
          </div>
        </div>
      )}
      <GenericCard
        isCollapsible={false}
        bodyContent={
          <>
            <div className="text-body font-medium text-semantic-text-secondary mb-1">Cluster name</div>
            <div className="text-body text-semantic-text-primary mb-2">{clusterName}</div>
            <div className="text-body font-medium text-semantic-text-secondary mb-1">Description</div>
            <div className="text-body text-semantic-text-primary mb-2">{description || '-'}</div>
          </>
        }
      />
      <div className="mb-6 space-y-4">
        <div className="text-body-large font-semibold mb-3">Configure cluster members</div>
        {selectedReflectors.map((r: Reflector, idx: number) => (
          <GenericCard
            key={r.id}
            isCollapsible={false}
            bodyContent={
              <>
                <div className="flex flex-col items-start gap-4 mb-2">
                  <span className="text-base font-semibold text-semantic-text-primary">{r.name}</span>
                  <div className="flex flex-row ">
                  {r.data?.region && (
                    <span className="">
                      <GenericTag label={r.data.region} variant="neutral" />
                    </span>
                  )}
                  <span className="ml-2"><GenericTag label={r.available ? 'Online' : 'Offline'} variant={r.available ? 'success' : 'error'} /></span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start mt-2">
                  <div>
                    <div className="text-body font-medium text-semantic-text-secondary mb-1">Role</div>
                    <CustomRadioGroup
                      name={`role-${r.id}`}
                      options={[
                        { value: 'primary', label: 'Primary' },
                      ]}
                      value={roles[r.id]?.role === 'primary' ? 'primary' : ''}
                      onChange={(val) => handleRoleChange(r.id, val)}
                      className="gap-6"
                      direction="row"
                    />
                  </div>
                  <div>
                    <div className="text-body font-medium text-semantic-text-secondary mb-1">Local address for HA/Clustering</div>
                    <GenericSearchableDropdown
                      items={interfaceAddresses[r.id] || []}
                      value={roles[r.id]?.address || ''}
                      onSelect={(val) => handleAddressChange(r.id, val)}
                      defaultLabel={loadingAddresses[r.id] ? "Loading addresses..." : "Select an address"}
                      className="w-full"
                      maxWidthClass="max-w-full"
                      size="md"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start mt-2">
                  <div></div>
                  <div>
                    <GenericInput
                      label="Port"
                      type="number"
                      placeholder="10117"
                      value={roles[r.id]?.port ?? 10117}
                      onChange={(val) => handlePortChange(r.id, val)}
                      className="w-full"
                    />
                  </div>
                </div>
              </>
            }
          />
        ))}
      </div>
      {!canSubmit && (
        <GenericInlineAlert variant="warning" className="mb-6">
          Please select exactly one reflector as Primary.
        </GenericInlineAlert>
      )}
      <GenericInlineAlert variant="info" className="mb-6">
        You are about to create a high-availability cluster with the configuration above. This will establish a HA/Clustering session between the selected CTX Reflectors.
      </GenericInlineAlert>
      <div className="flex flex-col md:flex-row justify-between gap-2">
        <GenericButton label="Cancel" variant="secondary" onClick={onBack} />
        <div className="flex gap-2 justify-end w-full md:w-auto">
          <GenericButton label="Back" variant="secondary" onClick={onBack} />
          <GenericButton 
            label={isSubmitting ? "Creating..." : "Create cluster"} 
            variant="brand" 
            onClick={onSubmit} 
            disabled={!canSubmit || isSubmitting}
            loading={isSubmitting}
          />
        </div>
      </div>
    </div>
  );
};

export default ClusterStep3; 